import type { CSSProperties } from 'react';
import type { FSRSState, UserKnownWord } from '@lingoping/core';

/** Hides text visually but keeps it for screen readers (e.g. headers of icon-only columns). */
export const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/** "1 card", "2 cards". */
export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/** Plain-language name for a card's learning stage (the scheduler's internal state names are jargon). */
export function cardStageLabel(state: FSRSState | string): string {
  switch (state) {
    case 'New':
      return 'New';
    case 'Learning':
      return 'Learning';
    case 'Review':
      return 'Learned';
    case 'Relearning':
      return 'Relearning';
    default:
      return String(state);
  }
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "Overdue", "Today", "Tomorrow" or a short local date for a card's next review. */
export function formatNextReview(dueIso: string, now: Date = new Date()): string {
  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return '–';
  const dayDiff = Math.round((startOfLocalDay(due) - startOfLocalDay(now)) / 86_400_000);
  if (dayDiff < 0) return 'Overdue';
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Tomorrow';
  return due.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Label for a calendar day string (YYYY-MM-DD, UTC like the stats use). Always formatted in UTC so
 * the weekday never shifts by one for users west of Greenwich.
 */
export function formatStatDay(dateStr: string, style: 'short' | 'long' = 'short'): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(
    undefined,
    style === 'short'
      ? { weekday: 'short', timeZone: 'UTC' }
      : { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }
  );
}

export interface KnownGroup {
  key: string;
  base: string;
  language: string;
  created_at: string;
  forms: UserKnownWord[];
}

/** Known words grouped by base lemma (e.g. "Tisch" + "Tische", or all forms of "gehen"). */
export function groupKnownWords(words: UserKnownWord[]): KnownGroup[] {
  const map = new Map<string, KnownGroup>();
  for (const w of words) {
    const base = w.base_lemma ?? w.lemma;
    const key = base.toLowerCase();
    const group = map.get(key) ?? {
      key,
      base,
      language: w.language,
      created_at: w.created_at,
      forms: [],
    };
    group.forms.push(w);
    map.set(key, group);
  }
  return Array.from(map.values());
}
